import type { ReactNode } from 'react';

// ─── Detection ────────────────────────────────────────────────────────────────

function isMarkdown(value: string): boolean {
  const lines = value.split('\n');
  let signals = 0;

  if (lines.some((l) => /^#{1,3} /.test(l))) signals++;
  if (/\*\*[^*\n]+\*\*/.test(value)) signals++;
  if (/(?<!\*)\*[^*\n]+\*(?!\*)/.test(value)) signals++;
  if (lines.some((l) => /^[-*] \S/.test(l.trimStart()))) signals++;
  if (lines.some((l) => /^\d+\. \S/.test(l.trimStart()))) signals++;
  if (lines.some((l) => /^> /.test(l))) signals++;
  if (/`[^`]+`/.test(value)) signals++;

  return signals >= 2;
}

function isJson(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return false;
  try {
    const parsed = JSON.parse(trimmed);
    return typeof parsed === 'object' && parsed !== null;
  } catch {
    return false;
  }
}

// ─── Inline markdown rendering ────────────────────────────────────────────────

function applyInline(text: string, baseKey: number): ReactNode {
  const parts: ReactNode[] = [];
  const regex = /(\*\*[^*]+\*\*|`[^`]+`|(?<!\*)\*[^*\n]+\*(?!\*))/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    const key = `${baseKey}-${match.index}`;
    if (token.startsWith('**')) {
      parts.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('`')) {
      parts.push(
        <code
          key={key}
          style={{
            fontFamily: 'monospace',
            fontSize: '0.85em',
            padding: '1px 5px',
            borderRadius: '3px',
            backgroundColor: 'var(--mds_brand_appearance_neutral_weak_background-color, #f0f2f5)',
            border: '1px solid var(--mds_brand_appearance_neutral_weak_border-color, #e0e0e0)',
          }}
        >
          {token.slice(1, -1)}
        </code>
      );
    } else {
      parts.push(<em key={key}>{token.slice(1, -1)}</em>);
    }
    lastIndex = match.index + token.length;
  }

  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : <>{parts}</>;
}

// ─── Markdown renderer ────────────────────────────────────────────────────────

function renderMarkdown(value: string): ReactNode {
  const lines = value.split('\n');
  const nodes: ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    if (line.startsWith('```')) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      const k = nodes.length;
      nodes.push(
        <pre
          key={k}
          style={{
            margin: '0.5rem 0',
            padding: '0.75rem',
            borderRadius: '6px',
            backgroundColor: 'var(--mds_brand_appearance_neutral_weak_background-color, #f0f2f5)',
            border: '1px solid var(--mds_brand_appearance_neutral_weak_border-color, #e0e0e0)',
            overflowX: 'auto',
            fontSize: '0.82rem',
          }}
        >
          <code style={{ fontFamily: 'monospace' }}>{codeLines.join('\n')}</code>
        </pre>
      );
      i++; // skip closing ```
      continue;
    }

    // Headings
    const h3 = line.match(/^### (.+)/);
    if (h3) {
      const k = nodes.length;
      nodes.push(
        <h3 key={k} style={{ margin: '0.5rem 0 0.15rem', fontSize: '0.9rem', fontWeight: 700 }}>
          {applyInline(h3[1], k)}
        </h3>
      );
      i++;
      continue;
    }
    const h2 = line.match(/^## (.+)/);
    if (h2) {
      const k = nodes.length;
      nodes.push(
        <h2 key={k} style={{ margin: '0.6rem 0 0.2rem', fontSize: '1rem', fontWeight: 700 }}>
          {applyInline(h2[1], k)}
        </h2>
      );
      i++;
      continue;
    }
    const h1 = line.match(/^# (.+)/);
    if (h1) {
      const k = nodes.length;
      nodes.push(
        <h1 key={k} style={{ margin: '0.75rem 0 0.25rem', fontSize: '1.15rem', fontWeight: 700 }}>
          {applyInline(h1[1], k)}
        </h1>
      );
      i++;
      continue;
    }

    // Blockquote
    if (/^> /.test(line)) {
      const quoteLines: string[] = [];
      while (i < lines.length && /^> /.test(lines[i])) {
        quoteLines.push(lines[i].slice(2));
        i++;
      }
      const k = nodes.length;
      nodes.push(
        <blockquote
          key={k}
          style={{
            margin: '0.4rem 0',
            paddingLeft: '0.75rem',
            borderLeft: '3px solid var(--mds_brand_appearance_neutral_weak_border-color, #ccc)',
            fontStyle: 'italic',
            color: 'var(--mds_brand_appearance_neutral_weak_text-color)',
          }}
        >
          {quoteLines.map((l, idx) => (
            <p key={idx} style={{ margin: '0.15rem 0', lineHeight: 1.6 }}>
              {applyInline(l, idx)}
            </p>
          ))}
        </blockquote>
      );
      continue;
    }

    // Unordered list
    if (/^[-*] \S/.test(line.trimStart())) {
      const items: string[] = [];
      while (i < lines.length && /^[-*] \S/.test(lines[i].trimStart())) {
        items.push(lines[i].trimStart().replace(/^[-*] /, ''));
        i++;
      }
      const k = nodes.length;
      nodes.push(
        <ul
          key={k}
          style={{ margin: '0.3rem 0', paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.1rem' }}
        >
          {items.map((l, idx) => (
            <li key={idx} style={{ lineHeight: 1.6 }}>
              {applyInline(l, idx)}
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // Ordered list
    if (/^\d+\. \S/.test(line.trimStart())) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\. \S/.test(lines[i].trimStart())) {
        items.push(lines[i].trimStart().replace(/^\d+\. /, ''));
        i++;
      }
      const k = nodes.length;
      nodes.push(
        <ol
          key={k}
          style={{ margin: '0.3rem 0', paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.1rem' }}
        >
          {items.map((l, idx) => (
            <li key={idx} style={{ lineHeight: 1.6 }}>
              {applyInline(l, idx)}
            </li>
          ))}
        </ol>
      );
      continue;
    }

    // Empty line — skip
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Paragraph
    const k = nodes.length;
    nodes.push(
      <p key={k} style={{ margin: '0.3rem 0', lineHeight: 1.6 }}>
        {applyInline(line, k)}
      </p>
    );
    i++;
  }

  return <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>{nodes}</div>;
}

// ─── JSON renderer ────────────────────────────────────────────────────────────

function renderJsonValue(val: unknown, depth: number, keyStr: string): ReactNode {
  if (val === null || val === undefined) {
    return (
      <span style={{ color: 'var(--mds_brand_appearance_neutral_weak_text-color)', fontStyle: 'italic' }}>
        null
      </span>
    );
  }
  if (typeof val === 'boolean') {
    return (
      <span style={{ fontWeight: 600, color: 'var(--mds_brand_appearance_info_default_text-color, #0369a1)' }}>
        {String(val)}
      </span>
    );
  }
  if (typeof val === 'number') {
    return <span style={{ fontWeight: 600 }}>{val}</span>;
  }
  if (typeof val === 'string') {
    return <span>{val}</span>;
  }
  if (Array.isArray(val)) {
    if (val.length === 0) {
      return (
        <span style={{ color: 'var(--mds_brand_appearance_neutral_weak_text-color)', fontStyle: 'italic' }}>
          empty
        </span>
      );
    }
    const isSimple = val.every((v) => typeof v !== 'object' || v === null);
    if (isSimple) {
      return (
        <ul style={{ margin: '0.2rem 0 0', paddingLeft: '1.1rem', display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
          {val.map((item, idx) => (
            <li key={idx} style={{ fontSize: '0.85rem', lineHeight: 1.5 }}>
              {renderJsonValue(item, depth + 1, `${keyStr}[${idx}]`)}
            </li>
          ))}
        </ul>
      );
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginTop: '0.25rem' }}>
        {val.map((item, idx) => (
          <div
            key={idx}
            style={{ paddingLeft: '0.5rem', borderLeft: '2px solid var(--mds_brand_appearance_neutral_weak_border-color, #e0e0e0)' }}
          >
            {renderJsonValue(item, depth + 1, `${keyStr}[${idx}]`)}
          </div>
        ))}
      </div>
    );
  }
  if (typeof val === 'object') {
    const entries = Object.entries(val as Record<string, unknown>);
    if (depth === 0) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {entries.map(([k, v]) => (
            <div
              key={k}
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: '6px',
                backgroundColor: 'var(--mds_brand_appearance_neutral_weak_background-color, #f0f2f5)',
                border: '1px solid var(--mds_brand_appearance_neutral_weak_border-color, #e0e0e0)',
              }}
            >
              <div
                style={{
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'var(--mds_brand_appearance_neutral_weak_text-color)',
                  marginBottom: '0.2rem',
                }}
              >
                {k.replace(/_/g, ' ')}
              </div>
              <div style={{ fontSize: '0.85rem', lineHeight: 1.5 }}>{renderJsonValue(v, depth + 1, k)}</div>
            </div>
          ))}
        </div>
      );
    }
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.2rem',
          paddingLeft: '0.6rem',
          borderLeft: '2px solid var(--mds_brand_appearance_neutral_weak_border-color, #e0e0e0)',
          marginTop: '0.15rem',
        }}
      >
        {entries.map(([k, v]) => (
          <div key={k} style={{ display: 'flex', gap: '0.3rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <span
              style={{
                fontSize: '0.68rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'var(--mds_brand_appearance_neutral_weak_text-color)',
                flexShrink: 0,
                paddingTop: '2px',
              }}
            >
              {k.replace(/_/g, ' ')}:
            </span>
            <div style={{ fontSize: '0.85rem', lineHeight: 1.5, flex: 1, minWidth: 0 }}>
              {renderJsonValue(v, depth + 1, k)}
            </div>
          </div>
        ))}
      </div>
    );
  }
  return null;
}

function renderJson(value: string): ReactNode {
  try {
    const parsed: unknown = JSON.parse(value.trim());
    return renderJsonValue(parsed, 0, 'root');
  } catch {
    return <p style={{ margin: 0, lineHeight: 1.6 }}>{value}</p>;
  }
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function TaskDescriptionView(value: string): ReactNode {
  if (!value?.trim()) return null;
  if (isMarkdown(value)) return renderMarkdown(value);
  if (isJson(value)) return renderJson(value);
  return <p style={{ margin: 0, lineHeight: 1.6 }}>{value}</p>;
}
